import { useMemo } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { Tooltip } from 'ui/tooltip/Tooltip';
import { Export24 } from 'ui/icons/aurora/24px/Export24';
import { spacing, colors } from 'ui/theme';
import { downloadAsCsvFile } from 'utils/csv';
import { addPositiveToast, addNegativeToast } from 'modules/toastContainer/utils';

import { useUserKeyParameter } from '../../hooks';
import { CSV_EXPORT_HEADERS } from './constants';
import { useEventFilterContext } from './contexts/filter';
import { useSummaryQueryContext } from './contexts/query';
import { useEventSelectionContext } from './contexts/selection';
import { ExtendedEventInfo } from './types';

const displayText = {
  disabledTooltip: 'Export will be available once the user has events',
};

const StyledButton = styled(Button)`
  color: ${colors.textPrimary};
  border: ${spacing.single} ${colors.grey15} solid;
`;

const header = CSV_EXPORT_HEADERS.map((csvHeader) => Object.values(csvHeader)[0]);

const handleExportCsv = (email: string, events: ExtendedEventInfo[], dispatch: any) => async () => {
  try {
    downloadAsCsvFile({
      rows: [
        header,
        ...events.map((event) => [
          event.eventName,
          event.eventType,
          event.count,
          event.eventPercent,
        ]),
      ],
      fileName: `Event Summary for ${email}.csv`,
    });
    dispatch(addPositiveToast(`Success! Exported event summary for ${email}.`));
  } catch (e) {
    dispatch(addNegativeToast(`Failed to export event summary for ${email}.`));
  }
};

export const CsvExportButton = () => {
  const email = useUserKeyParameter(true);
  const dispatch = useDispatch();
  const { selected, types } = useEventSelectionContext();
  const { isFiltering } = useEventFilterContext();
  const exportToCsv = useMemo(
    () => handleExportCsv(email, selected, dispatch),
    [email, selected, dispatch],
  );
  const { isLoading } = useSummaryQueryContext();
  const isFilteringData = isFiltering || selected.length < types.length;

  return (
    <Tooltip
      tooltipContent={displayText.disabledTooltip}
      isEnabled={!isLoading && !isFilteringData && !selected.length}
    >
      <StyledButton
        data-test="csv-export-button"
        variant={BUTTON_VARIANTS.SECONDARY}
        onClick={exportToCsv}
        size={BUTTON_SIZES.LARGE}
        isDisabled={isLoading || !selected.length}
      >
        <Export24 />
        <span>Export events summary</span>
      </StyledButton>
    </Tooltip>
  );
};
